import { Box, Typography } from "@mui/material";
import pic from "assets/splashpage/pro_splash_page.jpg";
import { AlertContext } from "contexts/Alert";
import {
  PRO_EMAIL_MONTHLY_LIMIT,
  SplashPage,
  SubscriptionContext,
} from "contexts/Subscription";
import { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Plans } from "schemas/payments";
import { routes } from "schemas/routes";

import { PRO_SALE_END, PRO_SALE_START } from "features/Home/Banner/helpers";
import { getSource } from "utils/tracking";
import { useSubscription } from "utils/useSubscription";

import SplashPageHome from "../components/Home";

const useProSaleCountdown = () => {
  const [display, setDisplay] = useState<string | null>(null);

  useEffect(() => {
    const tick = () => {
      const now = new Date();
      if (now < PRO_SALE_START || now >= PRO_SALE_END) {
        setDisplay(null);
        return;
      }
      const distance = PRO_SALE_END.getTime() - now.getTime();
      const days = Math.floor(distance / (1000 * 60 * 60 * 24));
      const hours = Math.floor(
        (distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
      );
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((distance % (1000 * 60)) / 1000);

      let text = "";
      if (days > 0) text += `${days}d `;
      if (hours > 0) text += `${hours}h `;
      if (minutes > 0) text += `${minutes}m `;
      text += `${seconds}s`;
      setDisplay(text);
    };

    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, []);

  return display;
};

const ProPlanSplashPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const source = getSource();
  const { setAlert } = useContext(AlertContext);
  const { subscription, isProPlan } = useContext(SubscriptionContext);
  const notQualified = Boolean(isProPlan && subscription?.isSubscribed);
  const { handleCheckout } = useSubscription();

  const checkOutForSplashPage = (plan: Plans) => {
    if (notQualified) {
      setAlert(
        "You already subscribed. Please go to Settings page to manage your subscription.",
        "info",
      );
      navigate(`/${routes.home}${location.search}`, { replace: true });
    } else {
      handleCheckout(
        Plans.PRO_YEARLY,
        source || "",
        window.location.pathname,
        SplashPage.pro,
      );
    }
  };

  const countdown = useProSaleCountdown();

  return (
    <SplashPageHome
      topBanner={
        countdown ? (
          <Box
            sx={{
              backgroundColor: "#1d70ff",

              textAlign: "center",
              py: 1,
              px: 2,
            }}
          >
            <Typography sx={{ fontSize: { xs: 12, md: 14 }, color: "white" }}>
              🔥 Pro on sale — lock in <strong>$21.66/mo</strong> before the
              price goes up. Offer ends in <strong>{countdown}</strong>
            </Typography>
          </Box>
        ) : undefined
      }
      checkOutForSplashPage={checkOutForSplashPage}
      sidebarPic={pic}
      planDetails={
        <>
          <Typography variant="h2">Bento's Pro Subscription</Typography>
          <Typography variant="h6">
            Supercharge your outreach. Earn more brand deals.
          </Typography>

          <Box sx={{ mt: 6 }}>
            <Typography sx={{ mb: 2 }}>
              <strong>Why Creators Choose Pro</strong>
            </Typography>
            🟢 <strong>Pitch at scale </strong> Send up to{" "}
            {PRO_EMAIL_MONTHLY_LIMIT} targeted pitches every month <br />
            🟢 <strong>Smart brand targeting </strong> Use exclusive filters to
            reach the right brands (e.g "Brands that work with UGC creators")
            <br />
            🟢 <strong>Reply Score Insights </strong>See which brands are most
            likely to respond before you hit send
            <br />
            🟢 <strong>Fresh, Personalized Recommendations </strong>Get updated
            brand matches every month, tailored to your niche <br />
            🟢 <strong>Cancel Anytime </strong>No contracts, no lock-ins. Just
            total flexibility <br />
          </Box>

          <Box sx={{ mt: 3 }}>
            <Typography sx={{ mb: 2 }}>
              <strong>The Results</strong>
            </Typography>
            <Typography>
              🚀 Pro members are{" "}
              <Box sx={{ color: "success.light" }} component="span">
                <strong>11x more likely</strong>
              </Box>{" "}
              to land a brand deals than other creators on Bento
            </Typography>
          </Box>
        </>
      }
      buttonText={
        <>
          starts at{" "}
          <Box sx={{ fontSize: 20 }} component="span">
            <strong>${countdown ? "21.66" : "25.00"}/month</strong>
          </Box>
        </>
      }
      fromRoutes={`${routes.pro}`}
      plan={Plans.PRO_YEARLY}
      notQualified={notQualified}
    />
  );
};

export default ProPlanSplashPage;
